import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import toast from "react-hot-toast";

/** ──────────────────────────────────────────────────────────────────────────
 *  Local cache (localStorage-only) with 5-minute TTL
 *  ────────────────────────────────────────────────────────────────────────── */
const isBrowser = typeof window !== "undefined";
const LOCAL_TTL_MS = 2 * 60 * 1000; // 2 minutes

function setLocalCache(key: string, value: any, ttlMs = LOCAL_TTL_MS) {
  const entry = { v: value, e: Date.now() + ttlMs };
  try {
    if (isBrowser) localStorage.setItem(key, JSON.stringify(entry));
  } catch {}
}

function getLocalCache<T = any>(key: string): T | null {
  try {
    if (!isBrowser) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { v, e } = JSON.parse(raw);
    if (typeof e === "number" && Date.now() > e) {
      localStorage.removeItem(key);
      return null; // expired
    }
    return v as T;
  } catch {
    return null;
  }
}

/** ──────────────────────────────────────────────────────────────────────────
 *  Types
 *  ────────────────────────────────────────────────────────────────────────── */
export interface AgentRequestPayload {
  intent: string;
  entities: {
    bucket: string;
    filename?: string;
    key?: string;
    content?: string;
    content_type?: string;
    data?: any;
  };
}

interface AgentResult<T = any> {
  data: T | null;
  statusCode: number;
  raw?: any;
}

/** ──────────────────────────────────────────────────────────────────────────
 *  Axios (same-origin) for website-routed backends (/agent, /upload, /api)
 *  ────────────────────────────────────────────────────────────────────────── */
const sharedHttpConfig = {
  timeout: 15000,
  withCredentials: true,
  headers: { Accept: "application/json" },
};

const attachHttpInterceptors = (client: AxiosInstance) => {
  client.interceptors.request.use((config) => {
    (config as any).metadata = { start: Date.now() };
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const meta = (response.config as any).metadata;
      if (meta) {
        const ms = Date.now() - meta.start;
        if (ms > 5000) {
          console.warn(
            `Slow request (${ms}ms): ${response.config.method?.toUpperCase()} ${
              response.config.url
            }`
          );
        }
      }
      return response;
    },
    (error) => {
      if (error.code === "ECONNABORTED") {
        toast("Request timed out", {
          duration: 4000,
          icon: "⏱️",
          style: { backgroundColor: "#03235E", color: "#fff" },
        });
      }
      return Promise.reject(error);
    }
  );
};

const http = axios.create(sharedHttpConfig);
export const apiHttp = axios.create({
  ...sharedHttpConfig,
  baseURL: "/api",
});

attachHttpInterceptors(http);
attachHttpInterceptors(apiHttp);

export const requestApi = async <T = any>(config: AxiosRequestConfig) => {
  const response = await apiHttp.request<T>(config);
  return response.data;
};

/** ──────────────────────────────────────────────────────────────────────────
 *  MCP Agent calls
 *  ────────────────────────────────────────────────────────────────────────── */
export const getAgentPage = async (page: string): Promise<AgentResult> => {
  const payload: AgentRequestPayload = {
    intent: "do_retrieve_json_file",
    entities: {
      bucket: "tc3-site",
      filename: `${page}.json`,
    },
  };

  try {
    const response = await http.post("/agent", payload, {
      headers: { "Content-Type": "application/json" },
    });

    const body = response.data;
    let pageData: any = null;
    if (body?.result?.data && typeof body.result.data === "object") {
      pageData = body.result.data;
    } else if (
      body?.data &&
      typeof body.data === "object" &&
      !("success" in body && "bucket" in body)
    ) {
      pageData = body.data;
    } else if (body?.result && typeof body.result === "object") {
      pageData = body.result.data || body.result;
    } else if (typeof body === "object") {
      pageData = body;
    }

    console.log(`Fetched page data for "${page}" from MCP agent.`);

    return { data: pageData ?? null, statusCode: response.status, raw: body };
  } catch (error: any) {
    if (error.response) {
      return {
        data: null,
        statusCode: error.response.status,
        raw: error.response.data,
      };
    }
    throw error;
  }
};

export const uploadImageForAgent = async (
  payload: AgentRequestPayload
): Promise<AgentResult> => {
  try {
    const response = await http.post("/upload", payload); // axios sets multipart boundary if payload is FormData
    return {
      data: response.data ?? null,
      statusCode: response.status,
      raw: response.data,
    };
  } catch (error: any) {
    console.error("Error uploading Image page data", error);
    if (error.response) {
      return {
        data: null,
        statusCode: error.response.status,
        raw: error.response.data,
      };
    }
    throw error;
  }
};

export const updateJsonForAgent = async (
  page: string,
  data: any
): Promise<AgentResult> => {
  const payload: AgentRequestPayload = {
    intent: "update_json_file",
    entities: {
      bucket: "tc3-site",
      filename: `${page}.json`,
      data,
    },
  };
  try {
    const response = await http.post("/agent", payload, {
      headers: { "Content-Type": "application/json" },
    });
    const body = response.data;
    let pageData: any = null;
    if (body?.result?.data && typeof body.result.data === "object") {
      pageData = body.result.data;
    } else if (
      body?.data &&
      typeof body.data === "object" &&
      !("success" in body && "bucket" in body)
    ) {
      pageData = body.data;
    } else if (body?.result && typeof body.result === "object") {
      pageData = body.result.data || body.result;
    } else if (typeof body === "object") {
      pageData = body;
    }
    return { data: pageData ?? null, statusCode: response.status, raw: body };
  } catch (error: any) {
    if (error.response) {
      return {
        data: null,
        statusCode: error.response.status,
        raw: error.response.data,
      };
    }
    throw error;
  }
};

/** ──────────────────────────────────────────────────────────────────────────
 *  AppService: cache-first (localStorage), then /agent; 5-min expiry
 *  ────────────────────────────────────────────────────────────────────────── */
class AppService {
  /**
   * getPage(page)
   * 1) Try localStorage (5-min TTL)
   * 2) If miss/expired, call /agent and store back into localStorage
   */
  async getPage(page: string) {
    let isLoading = true;
    let error: any = null;
    let source: "cache" | "agent" | "none" = "none";
    let data: any = null;
    let statusCode = 0;

    // 1. Local cache first
    const cached = getLocalCache(page);
    if (cached) {
      data = cached;
      source = "cache";
      isLoading = false;
      return {
        data,
        error,
        isLoading,
        source,
        statusCode: 200,
        wasFallback: false,
      };
    }

    // 2. MCP agent
    try {
      const agent = await getAgentPage(page);
      statusCode = agent.statusCode;
      if (agent.data) {
        data = agent.data;
        source = "agent";
        setLocalCache(page, data); // store with TTL
      }
    } catch (e: any) {
      error = e;
    }

    isLoading = false;
    return { data, error, isLoading, source, statusCode, wasFallback: false };
  }

  /** Optional helper to manually seed/override local cache */
  cache(page: string, data: any) {
    setLocalCache(page, data);
  }

  /** Optional helper to force-refresh next read */
  clearCache(page: string) {
    try {
      if (isBrowser) localStorage.removeItem(page);
    } catch {}
  }
}

export default AppService;
